
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faUserTie,
  faEnvelope,
  faStar,
  faCloudUploadAlt,
  faTrash,
  faSpinner,
  faExclamationTriangle,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
  faCalendarAlt,
  faChartBar,
  faListAlt,
  faEdit,
  faSave,
  faTimes,
  faClipboardList,
  faFilter,
  faUser,
  faPaperPlane,
  faBriefcase,
  faUsers,
  faUndo,
  faPlusCircle,
  faExclamationCircle,
  faBuilding, // Added for company icon
  faDownload, // Added for download icon
  faFileExcel, // Added for Excel download icon
  faBan, // Added for rejection icon
} from '@fortawesome/free-solid-svg-icons';
import { backend_url } from '../config';
import api from '../axiosInstance';
import RejectionModal from './RejectionModal';
import '../SavedCandidates.css';

function SavedCandidates() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const stageFilter = queryParams.get('stage');

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'similarityScore',
    direction: 'descending',
  });
  const [editingCandidateId, setEditingCandidateId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    interviewStatus: '',
    note: '',
    nextInterviewDate: '',
  });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentCandidateNotes, setCurrentCandidateNotes] = useState([]);
  const [currentCandidateNameForModal, setCurrentCandidateNameForModal] =
    useState('');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [statusFilter, setStatusFilter] = useState(stageFilter || 'All');
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);

  // New state for company filtering
  const [showCompanyFilter, setShowCompanyFilter] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('All');
  const [availableCompanies, setAvailableCompanies] = useState([]);

  // Rejection Modal State
  const [rejectionModalCandidate, setRejectionModalCandidate] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  const openRejectionModal = (candidate) => {
    setRejectionModalCandidate(candidate);
    setShowRejectionModal(true);
  };

  const handleRejectionSuccess = (updatedCandidate) => {
    setCandidates((prev) =>
      prev.map((c) => (c._id === updatedCandidate._id ? { ...c, ...updatedCandidate } : c))
    );
  };

  const stageColors = {
    'Not Started': '#adb5bd',
    'Resume Screening': '#a9def9',
    'Phone Screening': '#72d5fd',
    'Phone Interview': '#72d5fd',
    '1st Technical Round': '#2ab7ca',
    '2nd Technical Round': '#00A4CC',
    'Managerial Round': '#0077b6',
    'HR Round': '#2a9d8f',
    'Technical Assessment': '#2ab7ca',
    'On-site Interview': 'var(--coreops-secondary, #00A4CC)',
    'Reference Check': '#0077b6',
    'Offer Extended': '#2a9d8f',
    Hired: 'var(--coreops-success-text, #28a745)',
    Rejected: 'var(--coreops-error-text, #d9534f)',
    'On Hold': '#fdc500',
  };

  const interviewStages = [
    'Not Started',
    'Resume Screening',
    'Phone Screening',
    'Phone Interview',
    '1st Technical Round',
    '2nd Technical Round',
    'Managerial Round',
    'HR Round',
    'Technical Assessment',
    'On-site Interview',
    'Reference Check',
    'Offer Extended',
    'Hired',
    'Rejected',
    'On Hold',
  ];

  useEffect(() => {
    fetchCandidates();
  }, [location.pathname]);

  useEffect(() => {
    const currentStageFilterFromUrl = queryParams.get('stage');
    if (currentStageFilterFromUrl) {
      setStatusFilter(currentStageFilterFromUrl);
    } else if (!stageFilter) {
      setStatusFilter('All');
    }
  }, [location.search, stageFilter]);

  // Extract unique companies when candidates data changes
  useEffect(() => {
    if (candidates.length > 0) {
      const companies = new Set();
      candidates.forEach((candidate) => {
        if (
          candidate.companiesWorked &&
          Array.isArray(candidate.companiesWorked)
        ) {
          candidate.companiesWorked.forEach((company) => {
            if (company.companyName && company.companyName.trim()) {
              companies.add(company.companyName.trim());
            }
          });
        }
      });
      setAvailableCompanies(Array.from(companies).sort());
    }
  }, [candidates]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = location.pathname.toLowerCase().includes('qualified')
        ? '/qualified-candidates'
        : '/candidates';
      console.log(`Fetching from: ${endpoint}`);
      const response = await api.get(endpoint);
      const data = response.data;
      const candidatesArray = (
        Array.isArray(data) ? data : data.candidates || []
      ).map((c) => ({ ...c, similarityScore: c.similarityScore || 0 }));
      setCandidates(candidatesArray);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load candidates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this candidate permanently? This action cannot be undone.'
      )
    ) {
      return;
    }
    try {
      const endpoint = location.pathname.toLowerCase().includes('qualified')
        ? `/qualified-candidates/${candidateId}`
        : `/candidates/${candidateId}`;
      await api.delete(endpoint);
      setCandidates(candidates.filter((c) => c._id !== candidateId));
    } catch (err) {
      console.error('Error deleting candidate:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete candidate. Please try again.');
    }
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const startEditingCandidate = (candidate) => {
    setEditingCandidateId(candidate._id);
    setEditFormData({
      interviewStatus: candidate.interviewStatus || 'Not Started',
      note: '',
      nextInterviewDate: candidate.nextInterviewDate
        ? new Date(candidate.nextInterviewDate).toISOString().slice(0, 16)
        : '',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const cancelEditing = () => {
    setEditingCandidateId(null);
    setEditFormData({ interviewStatus: '', note: '', nextInterviewDate: '' });
  };

  const saveInterviewUpdate = async (candidateId) => {
    try {
      const endpoint = location.pathname.toLowerCase().includes('qualified')
        ? `/qualified-candidates/${candidateId}/interview-status`
        : `/candidates/${candidateId}/interview-status`;
      const response = await api.put(endpoint, {
        interviewStatus: editFormData.interviewStatus,
        note: editFormData.note,
        nextInterviewDate: editFormData.nextInterviewDate || null,
      });
      const updatedCandidateData = response.data;
      setCandidates(
        candidates.map((c) =>
          c._id === candidateId
            ? updatedCandidateData.candidate || updatedCandidateData
            : c
        )
      );
      cancelEditing();
    } catch (err) {
      console.error('Error updating interview status:', err);
      setError(err.message || 'Failed to update interview status.');
    }
  };

  const viewCandidateNotes = (candidate) => {
    setCurrentCandidateNotes(candidate.interviewNotes || []);
    setCurrentCandidateNameForModal(candidate.name);
    setShowNotesModal(true);
  };

  const formatDate = (dateString, includeTime = true) => {
    if (!dateString) return 'Not Set';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = true;
      }
      return date.toLocaleDateString('en-US', options);
    } catch (e) {
      return 'Date Error';
    }
  };

  // Helper function to check if candidate worked at a specific company
  const candidateWorkedAtCompany = (candidate, companyName) => {
    if (
      !candidate.companiesWorked ||
      !Array.isArray(candidate.companiesWorked)
    ) {
      return false;
    }
    return candidate.companiesWorked.some(
      (company) =>
        company.companyName &&
        company.companyName.toLowerCase().includes(companyName.toLowerCase())
    );
  };

  // Helper function to get all company names for a candidate as a searchable string
  const getCandidateCompanyNames = (candidate) => {
    if (
      !candidate.companiesWorked ||
      !Array.isArray(candidate.companiesWorked)
    ) {
      return '';
    }
    return candidate.companiesWorked
      .map((company) => company.companyName || '')
      .filter((name) => name.trim())
      .join(' ')
      .toLowerCase();
  };

  // New function to download company list
  const downloadCompanyList = () => {
    if (availableCompanies.length === 0) {
      alert('No companies found to download.');
      return;
    }

    // Create the text content
    const header = `Company List - Generated on ${new Date().toLocaleDateString()}\n`;
    const separator = '='.repeat(50) + '\n\n';
    const companiesText = availableCompanies
      .map((company, index) => `${index + 1}. ${company}`)
      .join('\n');

    const fullContent =
      header +
      separator +
      companiesText +
      `\n\nTotal Companies: ${availableCompanies.length}`;

    // Create and download the file
    const blob = new Blob([fullContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `company-list-${new Date().toISOString().split('T')[0]
      }.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // New function to download candidates as Excel
  const downloadCandidatesExcel = () => {
    if (sortedAndFilteredCandidates.length === 0) {
      alert('No candidates found to download.');
      return;
    }

    // Import XLSX library (you'll need to install it: npm install xlsx)
    // For now, we'll create a CSV format that Excel can open
    const csvHeaders = [
      'Name',
      'Email',
      'Phone',
      'Role Name',
      'Interview Status',
      'Similarity Score (%)',
      'Total Experience (Years)',
      'Main Domain',
      'Key Skills',
      'Companies Worked',
      'Resume Summary',
      'Next Interview Date',
      'Created Date',
      'Interview Notes Count',
    ];

    const csvData = sortedAndFilteredCandidates.map((candidate) => {
      // Format companies worked
      const companiesWorked =
        candidate.companiesWorked && Array.isArray(candidate.companiesWorked)
          ? candidate.companiesWorked
            .map(
              (company) =>
                `${company.companyName || 'N/A'} (${company.role || 'N/A'})`
            )
            .join('; ')
          : 'N/A';

      // Format key skills
      const keySkills =
        candidate.keySkills && Array.isArray(candidate.keySkills)
          ? candidate.keySkills.join(', ')
          : 'N/A';

      // Format dates
      const nextInterviewDate = candidate.nextInterviewDate
        ? formatDate(candidate.nextInterviewDate, false)
        : 'Not Set';

      const createdDate = candidate.createdAt
        ? formatDate(candidate.createdAt, false)
        : 'N/A';

      // Count interview notes
      const notesCount =
        candidate.interviewNotes && Array.isArray(candidate.interviewNotes)
          ? candidate.interviewNotes.length
          : 0;

      return [
        candidate.name || 'N/A',
        candidate.email || 'N/A',
        candidate.phone || 'N/A',
        candidate.roleName || 'N/A',
        candidate.interviewStatus || 'Not Started',
        candidate.similarityScore || 0,
        candidate.totalExperienceYears || 'N/A',
        candidate.mainDomain || 'N/A',
        keySkills,
        companiesWorked,
        candidate.resumeSummary
          ? candidate.resumeSummary.substring(0, 500) +
          (candidate.resumeSummary.length > 500 ? '...' : '')
          : 'N/A',
        nextInterviewDate,
        createdDate,
        notesCount,
      ];
    });

    // Convert to CSV format
    const csvContent = [csvHeaders, ...csvData]
      .map((row) =>
        row
          .map((field) => {
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            const stringField = String(field);
            if (
              stringField.includes(',') ||
              stringField.includes('"') ||
              stringField.includes('\n')
            ) {
              return '"' + stringField.replace(/"/g, '""') + '"';
            }
            return stringField;
          })
          .join(',')
      )
      .join('\n');

    // Add BOM for proper Excel UTF-8 handling
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with current date and filters
    let filename = `candidates-${new Date().toISOString().split('T')[0]}`;
    if (statusFilter !== 'All') {
      filename += `-${statusFilter.replace(/\s+/g, '-')}`;
    }
    if (companyFilter !== 'All') {
      filename += `-${companyFilter.replace(/\s+/g, '-')}`;
    }
    filename += '.csv';

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const sortedAndFilteredCandidates = useMemo(() => {
    let processed = [...candidates];

    // Filter by status
    processed =
      statusFilter === 'All'
        ? processed
        : processed.filter(
          (c) =>
            c.interviewStatus === statusFilter ||
            (!c.interviewStatus && statusFilter === 'Not Started')
        );

    // Filter by company
    if (companyFilter !== 'All') {
      processed = processed.filter((candidate) =>
        candidateWorkedAtCompany(candidate, companyFilter)
      );
    }

    // Filter by search term (including company names)
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      processed = processed.filter(
        (c) =>
          c.name?.toLowerCase().includes(lowerSearchTerm) ||
          c.email?.toLowerCase().includes(lowerSearchTerm) ||
          c.roleName?.toLowerCase().includes(lowerSearchTerm) ||
          c.mainDomain?.toLowerCase().includes(lowerSearchTerm) ||
          (c.keySkills &&
            c.keySkills.join(' ').toLowerCase().includes(lowerSearchTerm)) ||
          c.resumeSummary?.toLowerCase().includes(lowerSearchTerm) ||
          getCandidateCompanyNames(c).includes(lowerSearchTerm) // Added company search
      );
    }

    // Sort
    if (sortConfig.key) {
      processed.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (
          sortConfig.key === 'similarityScore' ||
          sortConfig.key === 'totalExperienceYears'
        ) {
          valA = Number(valA) || 0;
          valB = Number(valB) || 0;
        } else if (sortConfig.key === 'nextInterviewDate') {
          valA = valA
            ? new Date(valA).getTime()
            : sortConfig.direction === 'ascending'
              ? Infinity
              : -Infinity;
          valB = valB
            ? new Date(valB).getTime()
            : sortConfig.direction === 'ascending'
              ? Infinity
              : -Infinity;
        } else if (typeof valA === 'string') {
          valA = valA.toLowerCase();
          valB = String(valB || '').toLowerCase();
        } else if (valA === undefined || valA === null) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        } else if (valB === undefined || valB === null) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }

        if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return processed;
  }, [candidates, searchTerm, sortConfig, statusFilter, companyFilter]);

  const getSortIcon = (name) => {
    if (sortConfig.key === name) {
      return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
    }
    return faSort;
  };

  const sendIndividualMail = (candidate) => {
    const subject = encodeURIComponent(
      `Regarding Your Application - CoreOps.ai`
    );
    const body = encodeURIComponent(
      `Dear ${candidate.name},\n\nThank you for your interest in CoreOps.ai. We are currently reviewing applications and will be in touch regarding next steps.\n\nBest regards,\nThe CoreOps.ai Talent Team`
    );
    const mailtoLink = `mailto:${candidate.email}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');
  };

  const sendBulkMail = () => {
    if (selectedCandidateIds.length === 0) {
      alert('No candidates selected for bulk mail.');
      return;
    }
    const selectedForMail = candidates.filter(
      (c) =>
        selectedCandidateIds.includes(c._id) && c.email && c.email !== 'N/A'
    );
    if (selectedForMail.length === 0) {
      alert('None of the selected candidates have valid email addresses.');
      return;
    }
    const bccList = selectedForMail.map((c) => c.email).join(',');
    const subject = encodeURIComponent('Update from CoreOps.ai');
    const body = encodeURIComponent(
      `Dear Candidates,\n\nThank you for your interest in CoreOps.ai. We are currently reviewing applications and will be in touch with updates soon.\n\nBest regards,\nThe CoreOps.ai Talent Team`
    );
    const mailtoLink = `mailto:?bcc=${bccList}&subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');
  };

  const sendCalendarInvite = (candidate) => {
    const subject = `Interview: ${candidate.name} with CoreOps.ai`;
    const bodyHTML = `
      <div style="font-family: Inter, Arial, sans-serif; color: #2c3e50;">
        <h2 style="color: #0A2540;">Hi ${candidate.name},</h2>
        <p>This is an invitation to discuss your application further with the CoreOps.ai team.</p>
        <p>Please use the scheduling link that will be sent in a separate email, or reply to this message with your availability.</p>
        <p style="margin-top: 25px;">We look forward to our conversation!</p>
        <p style="margin-top: 30px;">Thanks,<br><strong>The CoreOps.ai Talent Team</strong></p>
      </div>
    `;
    const outlookCalendarLink = `https://outlook.office.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&to=${encodeURIComponent(
      candidate.email
    )}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      bodyHTML
    )}`;
    window.open(outlookCalendarLink, '_blank');
  };

  const toggleSingleCandidateSelection = (candidateId) => {
    setSelectedCandidateIds((prevSelected) =>
      prevSelected.includes(candidateId)
        ? prevSelected.filter((id) => id !== candidateId)
        : [...prevSelected, candidateId]
    );
  };

  const handleSelectAllFilteredCandidates = () => {
    if (
      selectedCandidateIds.length ===
      sortedAndFilteredCandidates.filter((c) => !c.error && !c.analysisError)
        .length
    ) {
      setSelectedCandidateIds([]);
    } else {
      const allFilteredIds = sortedAndFilteredCandidates
        .filter((c) => !c.error && !c.analysisError)
        .map((c) => c._id);
      setSelectedCandidateIds(allFilteredIds);
    }
  };

  const handleAddSelectedToShortlist = async () => {
    if (selectedCandidateIds.length === 0) {
      alert('No candidates selected to add to shortlist.');
      return;
    }
    try {
      alert(
        `Simulating: ${selectedCandidateIds.length} candidates added to a shortlist.`
      );
      setSelectedCandidateIds([]);
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  // Function to clear all filters
  const clearAllFilters = () => {
    setStatusFilter('All');
    setCompanyFilter('All');
    setSearchTerm('');
    setShowFilterOptions(false);
    setShowCompanyFilter(false);
  };

  return (
    <div className='coreops-page-container saved-candidates-page'>
      <div className='coreops-content-wrapper'>
        <header className='coreops-main-header results-sub-header'>
          <h1>
            <FontAwesomeIcon icon={faUsers} /> Candidate Pipeline
          </h1>
          <div className='coreops-results-summary-global'>
            {sortedAndFilteredCandidates.length} candidate(s) found
            {statusFilter !== 'All' ? ` (Status: ${statusFilter})` : ''}
            {companyFilter !== 'All' ? ` (Company: ${companyFilter})` : ''}
          </div>
        </header>

        <div className='coreops-controls-panel candidates-controls'>
          <div className='coreops-search-container'>
            <FontAwesomeIcon icon={faSearch} className='coreops-search-icon' />
            <input
              type='text'
              placeholder='Search by name, email, role, skills, or company...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='coreops-search-input'
            />
          </div>

          <div className='coreops-filter-section'>
            <button
              className={`coreops-button coreops-button-secondary filter-toggle-button ${showFilterOptions ? 'active' : ''
                } ${statusFilter !== 'All' ? 'filter-active-indicator' : ''}`}
              onClick={() => setShowFilterOptions(!showFilterOptions)}
            >
              <FontAwesomeIcon icon={faFilter} />
              {statusFilter !== 'All'
                ? ` Status: ${statusFilter}`
                : ' Filter by Status'}
            </button>

            <button
              className={`coreops-button coreops-button-secondary filter-toggle-button ${showCompanyFilter ? 'active' : ''
                } ${companyFilter !== 'All' ? 'filter-active-indicator' : ''}`}
              onClick={() => setShowCompanyFilter(!showCompanyFilter)}
            >
              <FontAwesomeIcon icon={faBuilding} />
              {companyFilter !== 'All'
                ? ` Company: ${companyFilter}`
                : ' Filter by Company'}
            </button>

            {/* Download Company List Button */}
            <button
              className='coreops-button coreops-button-secondary'
              onClick={downloadCompanyList}
              disabled={availableCompanies.length === 0}
              title={
                availableCompanies.length > 0
                  ? `Download list of ${availableCompanies.length} companies`
                  : 'No companies available to download'
              }
            >
              <FontAwesomeIcon icon={faDownload} />
              Download Company List ({availableCompanies.length})
            </button>

            {/* NEW: Download Candidates Excel Button */}
            <button
              className='coreops-button coreops-button-primary'
              onClick={downloadCandidatesExcel}
              disabled={sortedAndFilteredCandidates.length === 0}
              title={
                sortedAndFilteredCandidates.length > 0
                  ? `Download ${sortedAndFilteredCandidates.length} candidates as Excel file`
                  : 'No candidates available to download'
              }
            >
              <FontAwesomeIcon icon={faFileExcel} />
              Download Excel ({sortedAndFilteredCandidates.length})
            </button>
          </div>
        </div>

        {showFilterOptions && (
          <div className='coreops-filter-options-dropdown'>
            <h4>Filter by Interview Status:</h4>
            <div className='filter-pills-container'>
              <div
                key='All'
                className={`coreops-filter-pill ${statusFilter === 'All' ? 'active' : ''
                  }`}
                onClick={() => {
                  setStatusFilter('All');
                  setShowFilterOptions(false);
                }}
              >
                All Statuses
              </div>
              {interviewStages.map((stage) => (
                <div
                  key={stage}
                  className={`coreops-filter-pill ${statusFilter === stage ? 'active' : ''
                    }`}
                  onClick={() => {
                    setStatusFilter(stage);
                    setShowFilterOptions(false);
                  }}
                >
                  {stage}
                </div>
              ))}
            </div>
          </div>
        )}

        {showCompanyFilter && (
          <div className='coreops-filter-options-dropdown'>
            <h4>Filter by Company Experience:</h4>
            <div className='filter-pills-container'>
              <div
                key='All-Companies'
                className={`coreops-filter-pill ${companyFilter === 'All' ? 'active' : ''
                  }`}
                onClick={() => {
                  setCompanyFilter('All');
                  setShowCompanyFilter(false);
                }}
              >
                All Companies
              </div>
              {availableCompanies.map((company) => (
                <div
                  key={company}
                  className={`coreops-filter-pill ${companyFilter === company ? 'active' : ''
                    }`}
                  onClick={() => {
                    setCompanyFilter(company);
                    setShowCompanyFilter(false);
                  }}
                >
                  {company}
                </div>
              ))}
            </div>
          </div>
        )}

        {(showFilterOptions || showCompanyFilter) && (
          <div className='coreops-filter-options-dropdown'>
            <button
              className='coreops-button-inline clear-all-filters-button'
              onClick={clearAllFilters}
              title='Clear all filters and search'
            >
              <FontAwesomeIcon icon={faUndo} /> Clear All Filters
            </button>
          </div>
        )}

        {loading ? (
          <div className='coreops-message-container loading'>
            <FontAwesomeIcon icon={faSpinner} spin size='3x' />
            <p>Loading Candidates...</p>
          </div>
        ) : error ? (
          <div className='coreops-message-container error'>
            <FontAwesomeIcon icon={faExclamationTriangle} size='3x' />
            <p className='error-title'>Error Loading Data</p>
            <p>{error}</p>
            <button
              className='coreops-button coreops-button-primary'
              onClick={fetchCandidates}
            >
              <FontAwesomeIcon icon={faUndo} /> Retry
            </button>
          </div>
        ) : sortedAndFilteredCandidates.length === 0 ? (
          <div className='coreops-message-container empty'>
            <FontAwesomeIcon icon={faUsers} size='4x' />
            <p className='empty-title'>No Candidates Found</p>
            {searchTerm || statusFilter !== 'All' || companyFilter !== 'All' ? (
              <>
                <p className='empty-subtitle'>
                  Try adjusting your search or filter criteria.
                </p>
                <button
                  className='coreops-button coreops-button-secondary'
                  onClick={clearAllFilters}
                >
                  <FontAwesomeIcon icon={faUndo} /> Clear Search & Filters
                </button>
              </>
            ) : (
              <>
                <p className='empty-subtitle'>
                  Your candidate pipeline is currently empty.
                </p>
                <Link
                  to='/upload'
                  className='coreops-button coreops-button-primary'
                >
                  <FontAwesomeIcon icon={faCloudUploadAlt} /> Analyze New
                  Resumes
                </Link>
              </>
            )}
          </div>
        ) : (
          <>
            <div className='coreops-bulk-actions-bar'>
              <div className='select-all-container'>
                <input
                  type='checkbox'
                  className='coreops-checkbox'
                  title={
                    sortedAndFilteredCandidates.length === 0
                      ? 'No candidates to select'
                      : selectedCandidateIds.length ===
                        sortedAndFilteredCandidates.filter(
                          (c) => !c.error && !c.analysisError
                        ).length
                        ? 'Deselect all'
                        : 'Select all visible processable'
                  }
                  checked={
                    sortedAndFilteredCandidates.filter(
                      (c) => !c.error && !c.analysisError
                    ).length > 0 &&
                    selectedCandidateIds.length ===
                    sortedAndFilteredCandidates.filter(
                      (c) => !c.error && !c.analysisError
                    ).length
                  }
                  onChange={handleSelectAllFilteredCandidates}
                  disabled={
                    sortedAndFilteredCandidates.filter(
                      (c) => !c.error && !c.analysisError
                    ).length === 0
                  }
                  id='selectAllTableCheckbox'
                />
                <label htmlFor='selectAllTableCheckbox'>
                  {selectedCandidateIds.length > 0
                    ? `${selectedCandidateIds.length} Selected`
                    : 'Select All'}
                </label>
              </div>
              {selectedCandidateIds.length > 0 && (
                <div className='coreops-action-buttons-group'>
                  <button
                    className='coreops-button coreops-button-secondary'
                    onClick={sendBulkMail}
                    title='Send email to selected candidates'
                  >
                    <FontAwesomeIcon icon={faEnvelope} /> Email Selected
                  </button>
                  <button
                    className='coreops-button coreops-button-secondary'
                    onClick={handleAddSelectedToShortlist}
                    title='Add selected candidates to a shortlist'
                  >
                    <FontAwesomeIcon icon={faClipboardList} /> Add to Shortlist
                  </button>
                </div>
              )}
            </div>

            <div className='coreops-table-responsive-wrapper'>
              <table className='coreops-results-table saved-candidates-table'>
                <thead>
                  <tr>
                    <th style={{ width: '3%' }}></th>
                    <th
                      onClick={() => handleSort('name')}
                      style={{ width: '16%' }}
                    >
                      <FontAwesomeIcon
                        icon={faUserTie}
                        className='coreops-header-icon'
                      />
                      Name
                      <FontAwesomeIcon
                        icon={getSortIcon('name')}
                        className='coreops-sort-icon'
                      />
                    </th>
                    <th
                      onClick={() => handleSort('email')}
                      style={{ width: '16%' }}
                    >
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className='coreops-header-icon'
                      />
                      Email
                      <FontAwesomeIcon
                        icon={getSortIcon('email')}
                        className='coreops-sort-icon'
                      />
                    </th>
                    <th style={{ width: '12%' }}>
                      <FontAwesomeIcon
                        icon={faBuilding}
                        className='coreops-header-icon'
                      />
                      Companies
                    </th>
                    <th
                      onClick={() => handleSort('interviewStatus')}
                      style={{ width: '12%' }}
                    >
                      <FontAwesomeIcon
                        icon={faListAlt}
                        className='coreops-header-icon'
                      />
                      Status
                      <FontAwesomeIcon
                        icon={getSortIcon('interviewStatus')}
                        className='coreops-sort-icon'
                      />
                    </th>
                    <th
                      onClick={() => handleSort('similarityScore')}
                      style={{ width: '8%' }}
                    >
                      <FontAwesomeIcon
                        icon={faStar}
                        className='coreops-header-icon'
                      />
                      Score
                      <FontAwesomeIcon
                        icon={getSortIcon('similarityScore')}
                        className='coreops-sort-icon'
                      />
                    </th>
                    <th
                      style={{ width: '12%' }}
                      onClick={() => handleSort('roleName')}
                    >
                      <FontAwesomeIcon
                        icon={faBriefcase}
                        className='coreops-header-icon'
                      />
                      Target Role
                      <FontAwesomeIcon
                        icon={getSortIcon('roleName')}
                        className='coreops-sort-icon'
                      />
                    </th>
                    <th
                      style={{ width: '10%' }}
                      onClick={() => handleSort('nextInterviewDate')}
                    >
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className='coreops-header-icon'
                      />
                      Next Step
                      <FontAwesomeIcon
                        icon={getSortIcon('nextInterviewDate')}
                        className='coreops-sort-icon'
                      />
                    </th>
                    <th style={{ width: '11%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAndFilteredCandidates.map((candidate) => (
                    <React.Fragment key={candidate._id}>
                      <tr
                        className={
                          editingCandidateId === candidate._id
                            ? 'row-being-edited'
                            : ''
                        }
                      >
                        <td>
                          <input
                            type='checkbox'
                            className='coreops-checkbox'
                            checked={selectedCandidateIds.includes(
                              candidate._id
                            )}
                            onChange={() =>
                              toggleSingleCandidateSelection(candidate._id)
                            }
                            title={`Select ${candidate.name}`}
                          />
                        </td>
                        <td className='coreops-td-name' title={candidate.name}>
                          <Link
                            to={`/candidates/${candidate._id}`}
                            className='coreops-candidate-name-link'
                          >
                            {candidate.name}
                          </Link>
                        </td>
                        <td
                          className='coreops-td-email'
                          title={candidate.email}
                        >
                          {candidate.email}
                        </td>
                        <td className='coreops-td-companies'>
                          {candidate.companiesWorked &&
                            candidate.companiesWorked.length > 0 ? (
                            <div className='companies-list'>
                              {candidate.companiesWorked
                                .slice(0, 2)
                                .map((company, index) => (
                                  <span
                                    key={index}
                                    className='company-tag'
                                    title={`${company.companyName} - ${company.role}`}
                                  >
                                    {company.companyName}
                                  </span>
                                ))}
                              {candidate.companiesWorked.length > 2 && (
                                <span
                                  className='more-companies'
                                  title={`+${candidate.companiesWorked.length - 2
                                    } more companies`}
                                >
                                  +{candidate.companiesWorked.length - 2}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className='no-companies'>N/A</span>
                          )}
                        </td>
                        <td>
                          <div className='coreops-status-cell'>
                            <span
                              className='coreops-status-pill'
                              style={{
                                backgroundColor:
                                  stageColors[
                                  candidate.interviewStatus || 'Not Started'
                                  ],
                              }}
                              title={`Status: ${candidate.interviewStatus || 'Not Started'
                                }`}
                            >
                              {candidate.interviewStatus || 'Not Started'}
                            </span>
                          </div>
                        </td>
                        <td
                          className='coreops-td-score'
                          title={`Match Score: ${candidate.similarityScore || 0
                            }`}
                        >
                          {candidate.similarityScore || 0}%
                        </td>
                        <td title={candidate.roleName || 'N/A'}>
                          {candidate.roleName || 'N/A'}
                        </td>
                        <td
                          title={
                            candidate.nextInterviewDate
                              ? formatDate(candidate.nextInterviewDate)
                              : 'Not Set'
                          }
                        >
                          {candidate.nextInterviewDate
                            ? formatDate(candidate.nextInterviewDate)
                            : 'Not Set'}
                        </td>
                        <td className='coreops-td-actions'>
                          <button
                            className='coreops-button-icon'
                            onClick={() => startEditingCandidate(candidate)}
                            title='Update Status/Notes'
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          {candidate.interviewNotes &&
                            candidate.interviewNotes.length > 0 && (
                              <button
                                className='coreops-button-icon'
                                onClick={() => viewCandidateNotes(candidate)}
                                title='View Interview Notes'
                              >
                                <FontAwesomeIcon icon={faClipboardList} />
                              </button>
                            )}
                          <button
                            className='coreops-button-icon'
                            onClick={() => sendIndividualMail(candidate)}
                            title={`Email ${candidate.name}`}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} />
                          </button>
                          <br />
                          <button
                            className='coreops-button-icon'
                            onClick={() => sendCalendarInvite(candidate)}
                            title={`Schedule Interview with ${candidate.name}`}
                          >
                            <FontAwesomeIcon icon={faCalendarAlt} />
                          </button>
                          <button
                            className='coreops-button-icon delete'
                            onClick={() => openRejectionModal(candidate)}
                            title='Reject Candidate & Send Mail'
                            style={{ color: '#d9534f' }}
                          >
                            <FontAwesomeIcon icon={faBan} />
                          </button>
                          <button
                            className='coreops-button-icon delete'
                            onClick={() => handleDeleteCandidate(candidate._id)}
                            title='Delete Candidate'
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                      {editingCandidateId === candidate._id && (
                        <tr className='coreops-edit-row'>
                          <td colSpan='9'>
                            <div className='coreops-edit-form-container'>
                              <h4 className='coreops-edit-form-title'>
                                Update Status for {candidate.name}
                              </h4>
                              <div className='coreops-form-grid'>
                                <div className='coreops-form-group'>
                                  <label
                                    htmlFor={`interviewStatus-${candidate._id}`}
                                    className='coreops-form-label'
                                  >
                                    Interview Stage:
                                  </label>
                                  <select
                                    id={`interviewStatus-${candidate._id}`}
                                    name='interviewStatus'
                                    value={editFormData.interviewStatus}
                                    onChange={handleEditFormChange}
                                    className='coreops-select'
                                  >
                                    {interviewStages.map((stage) => (
                                      <option key={stage} value={stage}>
                                        {stage}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className='coreops-form-group'>
                                  <label
                                    htmlFor={`nextInterviewDate-${candidate._id}`}
                                    className='coreops-form-label'
                                  >
                                    Next Interview Date:
                                  </label>
                                  <input
                                    type='datetime-local'
                                    id={`nextInterviewDate-${candidate._id}`}
                                    name='nextInterviewDate'
                                    value={editFormData.nextInterviewDate}
                                    onChange={handleEditFormChange}
                                    className='coreops-input'
                                  />
                                </div>
                                <div className='coreops-form-group full-width'>
                                  <label
                                    htmlFor={`interviewNote-${candidate._id}`}
                                    className='coreops-form-label'
                                  >
                                    Add Note (for this update):
                                  </label>
                                  <textarea
                                    id={`interviewNote-${candidate._id}`}
                                    name='note'
                                    value={editFormData.note}
                                    onChange={handleEditFormChange}
                                    placeholder='Enter notes about this stage or update...'
                                    className='coreops-textarea'
                                    rows='3'
                                  />
                                </div>
                              </div>
                              <div className='coreops-edit-form-actions'>
                                <button
                                  className='coreops-button coreops-button-secondary'
                                  onClick={cancelEditing}
                                >
                                  <FontAwesomeIcon icon={faTimes} /> Cancel
                                </button>
                                <button
                                  className='coreops-button coreops-button-primary'
                                  onClick={() =>
                                    saveInterviewUpdate(candidate._id)
                                  }
                                >
                                  <FontAwesomeIcon icon={faSave} /> Save Update
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {showNotesModal && (
          <div
            className='coreops-modal-overlay'
            onClick={() => setShowNotesModal(false)}
          >
            <div
              className='coreops-modal-content'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='coreops-modal-header'>
                <h3 className='coreops-modal-title'>
                  <FontAwesomeIcon icon={faClipboardList} /> Interview Notes for{' '}
                  {currentCandidateNameForModal}
                </h3>
                <button
                  className='coreops-modal-close-button'
                  onClick={() => setShowNotesModal(false)}
                  title='Close'
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className='coreops-modal-body scrollable'>
                {currentCandidateNotes.length > 0 ? (
                  currentCandidateNotes
                    .slice()
                    .reverse()
                    .map((note, index) => (
                      <div key={index} className='coreops-note-item'>
                        <div className='coreops-note-header'>
                          <span
                            className='coreops-note-stage'
                            style={{
                              backgroundColor:
                                stageColors[note.stage || 'Not Started'],
                            }}
                          >
                            {note.stage || 'General Note'}
                          </span>
                          <span className='coreops-note-date'>
                            {formatDate(note.createdAt)}
                          </span>
                        </div>
                        <p className='coreops-note-text'>{note.note}</p>
                      </div>
                    ))
                ) : (
                  <p className='no-notes-text'>
                    No notes available for this candidate.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        <RejectionModal
          show={showRejectionModal}
          candidate={rejectionModalCandidate}
          onClose={() => {
            setShowRejectionModal(false);
            setRejectionModalCandidate(null);
          }}
          onSuccess={handleRejectionSuccess}
          isQualified={location.pathname.toLowerCase().includes('qualified')}
        />

        <footer className='coreops-footer results-sub-footer'>
          <p>© {new Date().getFullYear()} COREHire - Candidate Management</p>
        </footer>
      </div>
    </div>
  );
}

export default SavedCandidates;
